import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { ModalDraftContext, type ModalDraft } from './unsavedChanges';
import { createPortal } from 'react-dom';
import { X, Check, AlertCircle, LoaderCircle } from 'lucide-react';

export function Modal({ title, subtitle, children, onClose, wide = false, className = '', closeLabel = 'Close dialog', closeText = 'Close' }: { title: string; subtitle?: string; children: ReactNode; onClose: () => void; wide?: boolean; className?: string; closeLabel?: string; closeText?: string }) {
  const id = useId();
  const panel = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  const draft = useRef<ModalDraft | null>(null);
  const [confirmExit,setConfirmExit] = useState(false);
  const [dismissLocked,setDismissLocked] = useState(false);
  const confirmRef = useRef(false);
  const saveAndClose = useRef(false);
  const editingFocus = useRef<HTMLElement | null>(null);
  const keepEditing = () => {
    confirmRef.current=false;setConfirmExit(false);
    requestAnimationFrame(()=>editingFocus.current?.focus({preventScroll:true}));
  };
  const requestClose = () => {
    if(dismissLocked)return;
    if(confirmRef.current){keepEditing();return;}
    if(draft.current?.busy)return;
    if(draft.current?.dirty){
      editingFocus.current=document.activeElement as HTMLElement;
      confirmRef.current=true;setConfirmExit(true);
    }else onClose();
  };
  close.current = requestClose;
  const context = useMemo(()=>({draft,setDismissLocked,settled:(saved:boolean)=>{
    const shouldClose=saveAndClose.current;saveAndClose.current=false;
    if(saved&&shouldClose)onCloseRef.current();
  }}),[]);
  const onCloseRef=useRef(onClose);onCloseRef.current=onClose;
  useEffect(()=>{if(confirmExit)panel.current?.querySelector<HTMLButtonElement>('[data-keep-editing]')?.focus();},[confirmExit]);
  function saveChanges(){
    confirmRef.current=false;setConfirmExit(false);
    requestAnimationFrame(()=>{
      const current=draft.current;
      if(!current||current.busy)return;
      if(current.form&&!current.form.reportValidity())return;
      saveAndClose.current=true;
      if(current.save)current.save();else current.form?.requestSubmit();
    });
  }
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const focusable = () => Array.from(panel.current?.querySelectorAll<HTMLElement>('button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, a[href], [tabindex="0"]') ?? []).filter(element=>element.getClientRects().length>0);
    const frame = requestAnimationFrame(() => {
      const target = window.matchMedia('(max-width: 760px)').matches ? panel.current : focusable().find(e => e.tagName === 'INPUT') ?? panel.current;
      target?.focus({ preventScroll: true });
    });
    const key = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.preventDefault(); close.current(); }
      if (event.key === 'Tab') {
        const items = focusable(); const first = items[0]; const last = items.at(-1);
        if (!first) { event.preventDefault(); return; }
        if (event.shiftKey && (document.activeElement === first || document.activeElement === panel.current)) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', key);
    return () => { cancelAnimationFrame(frame); document.body.style.overflow = overflow; document.removeEventListener('keydown', key); previous?.focus(); };
  }, []);
  return createPortal(<div className={`modal-backdrop ${className ? `${className}-backdrop` : ''}`} onMouseDown={event => { if (event.target === event.currentTarget) requestClose(); }}>
    <div className={`modal ${wide ? 'modal-wide' : ''} ${className}`} ref={panel} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby={id} onInvalidCapture={event=>{const section=(event.target as HTMLElement).closest('details');if(section)section.open=true;}}>
      <div className="modal-heading"><div><h2 id={id}>{confirmExit?'Save changes before closing?':title}</h2>{!confirmExit && subtitle && <p>{subtitle}</p>}</div>{!dismissLocked&&<button type="button" className="text-button modal-close-label" aria-label={closeLabel} onClick={requestClose}><X size={20} /><span>{closeText}</span></button>}</div>
      <ModalDraftContext.Provider value={context}>
        {confirmExit&&<div className="unsaved-changes" role="alertdialog" aria-label="Unsaved changes" aria-describedby={`${id}-unsaved`}>
          <p id={`${id}-unsaved`}>You have unsaved changes. Save them before closing, or discard them.</p>
          <div className="unsaved-actions">
            <button type="button" className="button primary" onClick={saveChanges}><Check size={18}/>Save changes</button>
            <button type="button" className="button danger-outline" onClick={()=>{confirmRef.current=false;onClose();}}>Discard changes</button>
            <button type="button" className="button secondary" data-keep-editing onClick={keepEditing}>Keep editing</button>
          </div>
        </div>}
        {className.split(' ').includes('seizure-focus') ? children : <div className="modal-content" hidden={confirmExit}>{children}</div>}
      </ModalDraftContext.Provider>
    </div>
  </div>, document.body);
}
export function Field({ label, help, children, required }: { label: string; help?: string; children: ReactNode; required?: boolean }) {
  return <label className="field"><span>{label}{required && <span className="required"> *</span>}</span>{children}{help && <small>{help}</small>}</label>;
}
export function FormError({ error }: { error: string }) { return error ? <div className="form-error" role="alert"><AlertCircle size={18} />{error}</div> : null; }
export function SubmitButton({ busy, children }: { busy?: boolean; children: ReactNode }) { return <button className="button primary" type="submit" disabled={busy}>{busy ? <LoaderCircle className="spin" size={18} /> : <Check size={18} />}{busy ? 'Saving…' : children}</button>; }
export function EmptyState({ icon, title, description, action, illustration }: { icon: ReactNode; title: string; description: string; action?: ReactNode; illustration?: string }) { return <div className="empty-state">{illustration?<img className="empty-illustration" src={illustration} width="240" height="240" loading="lazy" alt=""/>:<span className="empty-icon">{icon}</span>}<h3>{title}</h3><p>{description}</p>{action}</div>; }
export function Badge({ children, tone = 'muted' }: { children: ReactNode; tone?: 'green' | 'peach' | 'muted' | 'red' }) { return <span className={`badge badge-${tone}`}>{children}</span>; }
